import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { CustomDivider } from '../Utils/CustomDivider';
import { Download2White, LeftArrowWhite } from 'Images';
import { OneOrTwoButtons } from 'Components/OneOrTwoButtons';
import { ServerError } from '../ServerError';
import { NoData } from './NoData';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import {
  IPayslips,
  getPayslipFile,
  getSummarizedPaychecks,
} from 'API/Paychecks/paychecks';
import { ShowPaychecksToggle } from './ShowPaychecksToggle';
import { PaychecksSwitcher } from './PaychecksSwitcher';
import { PaychecksChart } from './PaychecksChart';
import { PaychecksDetails } from './PaychecksDetails';
import { useNavigate } from 'react-router-dom';
import { downloadFile } from 'Utils';
import { HTTP_STATUS_CODES } from 'Enum/HttpStatusCodes';

export const UserPaychecks = () => {
  const SummarizedPaychecksMaxAmount = 12;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentPaycheck, setCurrentPaycheck] = useState<IPayslips | null>(
    null
  );
  const [showPaychecks, setShowPaychecks] = useState(false);
  const [paychecks, setPaychecks] = useState<IPayslips[]>([]);

  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.Home).userPaychecks;
  const [showServerError, setShowServerError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (paychecks && paychecks.length > 0) {
      setCurrentPaycheck(paychecks[currentIndex]);
    }
  }, [currentIndex, paychecks]);

  const imposterId = useAppSelector(({ imposter: { imposterId } }) => {
    return imposterId;
  });

  const getData = useCallback(async () => {
    const paychecks = await getSummarizedPaychecks(
      SummarizedPaychecksMaxAmount
    );
    if (paychecks && paychecks.status === HTTP_STATUS_CODES.OK)
      setPaychecks(paychecks.data);
    else if (
      paychecks &&
      paychecks.status === HTTP_STATUS_CODES.InternalServerError
    )
      setShowServerError(true);
  }, []);

  useEffect(() => {
    getData();
  }, [getData, imposterId]);

  const downloadPdf = async () => {
    if (!currentPaycheck?.fileId) return;
    const fileData = await getPayslipFile(currentPaycheck.fileId);
    if (!fileData) return;
    const sourceBase64 = fileData.file;
    if (sourceBase64)
      downloadFile(
        sourceBase64,
        '.pdf',
        `${dictionary.paycheck} ${currentPaycheck?.month}/${currentPaycheck?.year}`,
        false
      );
  };

  return (
    <Stack sx={{ position: 'relative', mb: '32px' }}>
      <Box
        sx={
          isDesktop
            ? undefined
            : {
                position: 'absolute',
                height: 'calc(100% + 48px)',
                width: '100vw',
                top: '-24px',
                right: '-24px',
                bgcolor: '#fff',
                zIndex: -1,
              }
        }
      />
      <Typography variant={isDesktop ? 'h5' : 'h3'} component={'h2'}>
        {dictionary.title}
      </Typography>
      <CustomDivider />
      <Stack
        sx={{
          backgroundColor:
            paychecks && paychecks.length > 0 ? '#fff' : 'primary.shade_100',
          mt: 2,
          p: isDesktop ? '24px 24px 40px 24px' : '0 0 24px 0',
          height: '570px',
          borderRadius: '16px',
          justifyContent:
            paychecks && paychecks.length > 0 ? 'space-between' : 'center',
        }}
      >
        {paychecks && paychecks.length > 0 ? (
          <>
            <ShowPaychecksToggle
              setShowPaychecks={setShowPaychecks}
              showPaychecks={showPaychecks}
            />
            <PaychecksSwitcher
              currentIndex={currentIndex}
              currentPaycheck={currentPaycheck}
              paychecks={paychecks}
              setCurrentIndex={setCurrentIndex}
            />
            <PaychecksChart
              currentPaycheck={currentPaycheck}
              showPaychecks={showPaychecks}
            />
            <PaychecksDetails
              currentPaycheck={currentPaycheck}
              showPaychecks={showPaychecks}
            />
            <OneOrTwoButtons
              width='100%'
              leftButtonRole='link'
              leftButton={{
                label: dictionary.allPaychecks,
                labelColor: '#fff',
                onClick: () => navigate('/paychecks'),
                icon: LeftArrowWhite,
              }}
              rightButton={{
                label: dictionary.downloadPaycheck,
                labelColor: '#fff',
                onClick: () => downloadPdf(),
                icon: Download2White,
              }}
              buttonsBackground
              buttonsBackgroundColor='purple2.shade_500'
            />
          </>
        ) : showServerError ? (
          <ServerError size='small' />
        ) : (
          <NoData />
        )}
      </Stack>
    </Stack>
  );
};
